/**
 * Snapshot Formatter Rendering
 *
 * Snapshot-level rendering, frontmatter generation, expansion flows, and
 * rendered-unit counting.
 *
 * @module
 */

import { isSubstrateV2, parseSnapshot, type SnapshotElement } from './snapshot-analyzer.js';
import {
  formatContentCards,
  formatContextAnnotations,
  formatDatepicker,
} from './snapshot-formatter-content.js';
import {
  extractEditingContextsFromElements,
  formatEditingContexts,
} from './snapshot-editing-context.js';
import { countRenderedGridItems, formatGridOutput } from './snapshot-formatter-grid.js';
import { populateSearchContext, sortMatchesByContext } from './snapshot-formatter-search.js';
import {
  buildSectionTree,
  composeTriggerLabel,
  expandSectionByRef,
  findSectionByRef,
  markMatchingSections,
  sortSectionsByFocus,
} from './snapshot-formatter-sections.js';
import {
  FORM_INPUT_ROLES,
  INTERACTIVE_ROLES,
  getDisplayText,
  type ElementHintAnnotation,
  type FormattedElement,
  type GridInfo,
  type SearchMatch,
  type Section,
  type SemanticSnapshotOptions,
} from './snapshot-formatter-shared.js';
import { fingerprintAllElements } from './element-fingerprint.js';

const COLOR_NAMES = [
  'red',
  'green',
  'blue',
  'yellow',
  'magenta',
  'cyan',
  'orange',
  'purple',
  'teal',
  'pink',
];

export function findActiveElement(elements: SnapshotElement[]): SnapshotElement | null {
  let deepestActive: SnapshotElement | null = null;

  function findDeepest(element: SnapshotElement, isRoot: boolean): void {
    for (const child of element.children) {
      findDeepest(child, false);
    }
    if (element.attributes['active'] !== undefined || element.rawLine.includes('[active]')) {
      // Playwright often marks the document/root generic as active when no
      // meaningful control has focus. Treat that as no active element so
      // ordinary page content, including virtualized tables, is not collapsed.
      if (isRoot && element.role === 'generic' && !element.text) {
        return;
      }
      if (!deepestActive) {
        deepestActive = element;
      }
    }
  }

  for (const element of elements) {
    findDeepest(element, true);
  }

  return deepestActive;
}

function formatElementLine(el: FormattedElement, indent: number): string {
  const indentStr = '  '.repeat(indent);
  const parts: string[] = [];

  if (el.isActive) {
    parts.push('ACTIVE →');
  }

  parts.push(el.ref ? `[${el.ref}]` : '[no-ref]');
  parts.push(el.role);

  if (el.isClickable) {
    parts.push('[clickable]');
  }

  if (el.isDisabled) {
    parts.push('[disabled]');
  }
  if (el.isReadonly) {
    parts.push('[readonly]');
  }
  for (const attr of el.attributes) {
    if (attr !== 'disabled' && attr !== 'readonly') {
      parts.push(`[${attr}]`);
    }
  }

  if (el.fieldLabel) {
    parts.push(`| field: "${el.fieldLabel}"`);
  }

  // Substrate-v2 render-only suffix (e.g. duplicate-label disambiguator).
  // Concatenated onto the label/name token at emission time so substrate
  // fields stay canonical. Empty for v1 captures (which still mutate
  // `name`/`label` directly to preserve byte-identical output).
  const decoration = el.displayDecoration ?? '';

  if (el.label && el.value) {
    parts.push(`| label: "${el.label}${decoration}" | value: "${el.value}"`);
  } else if (el.value) {
    parts.push(`| value: "${el.value}"`);
  } else if (el.label) {
    parts.push(`| label: "${el.label}${decoration}"`);
  } else if (el.name) {
    parts.push(`"${el.name}${decoration}"`);
  }

  if (el.status) {
    parts.push(`| status: "${el.status}"`);
  }

  if (
    (el.role === 'textbox' || el.role === 'combobox' || el.role === 'searchbox') &&
    el.inputValue
  ) {
    parts.push(`| input: "${el.inputValue}"`);
  }

  if (el.options?.length) {
    const MAX_DISPLAY = 8;
    const displayed = el.options.slice(0, MAX_DISPLAY);
    const optionsList = displayed.map((o) => `"${o}"`).join(', ');
    const suffix =
      el.options.length > MAX_DISPLAY ? `, ... +${el.options.length - MAX_DISPLAY} more` : '';
    if (el.role === 'combobox') {
      parts.push(`| options: [${optionsList}${suffix}] → use browser_select_option`);
    } else {
      parts.push(`| options: [${optionsList}${suffix}]`);
    }
  }

  // Append element hint if present
  if (el.hint) {
    const prefix =
      el.hintCategory === 'format'
        ? 'format hint'
        : el.hintCategory === 'pattern'
          ? 'pattern'
          : el.hintCategory === 'guidance'
            ? 'guidance'
            : el.hintAnchoredToParent && el.hintElement
              ? `hint on ${el.hintElement}`
              : 'hint';
    parts.push(`(${prefix}: ${el.hint})`);
    if (el.hintHasBody) {
      parts.push('[+docs]');
    }
  }

  return indentStr + parts.join(' ');
}

function collectAllElementsFlat(section: Section): FormattedElement[] {
  const elements: FormattedElement[] = [...section.elements];
  for (const subsection of section.subsections) {
    elements.push(...collectAllElementsFlat(subsection));
  }
  return elements;
}

const COLLAPSED_GRID_PREVIEW_ROWS = 3;
const COLLAPSED_GRID_PREVIEW_CELLS = 3;
const COLLAPSED_GRID_DRAFT_PREVIEW_CELLS = 8;
const COLLAPSED_GRID_PREVIEW_CELL_CHARS = 80;
const DRAFT_ROW_PREVIEW_RE =
  /\b(new|draft|unsaved|editing|edited|pending|validation|invalid|required|error)\b/i;

function truncatePreviewText(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
}

function formatCollapsedGridPreview(grid: GridInfo, indent: number): string[] {
  const indentStr = '  '.repeat(indent);
  const lines: string[] = [];
  const colInfo = grid.columns.length > 0 ? `${grid.columns.length} columns` : 'unknown columns';
  lines.push(`${indentStr}GRID [${grid.ref}]: ${grid.totalRows} rows, ${colInfo}`);

  const draftRows = grid.rows.filter((row) => row.label && DRAFT_ROW_PREVIEW_RE.test(row.label));
  const previewRows = [...draftRows, ...grid.rows]
    .filter(
      (row, index, rows) => rows.findIndex((candidate) => candidate.ref === row.ref) === index
    )
    .slice(0, COLLAPSED_GRID_PREVIEW_ROWS);
  for (const row of previewRows) {
    const cellLimit =
      row.label && DRAFT_ROW_PREVIEW_RE.test(row.label)
        ? COLLAPSED_GRID_DRAFT_PREVIEW_CELLS
        : COLLAPSED_GRID_PREVIEW_CELLS;
    const cellParts = row.cells
      .filter((cell) => cell.hasValue && cell.value.trim())
      .slice(0, cellLimit)
      .map((cell) => {
        const value = truncatePreviewText(cell.value.trim(), COLLAPSED_GRID_PREVIEW_CELL_CHARS);
        return cell.column ? `${cell.column}: ${value}` : value;
      });
    if (cellParts.length === 0) continue;
    const rowLabel = row.label ? ` "${row.label}"` : '';
    lines.push(`${indentStr}  Row [${row.ref}]${rowLabel}: ${cellParts.join(' | ')}`);
  }

  const remainingRows = grid.totalRows - previewRows.length;
  if (remainingRows > 0) {
    lines.push(`${indentStr}  ... +${remainingRows} more rows`);
  }

  return lines;
}

function formatSubsectionSummary(subsection: Section): string {
  const refPart = subsection.ref ? ` [${subsection.ref}]` : '';

  if (subsection.gridInfo) {
    const colInfo =
      subsection.gridInfo.columns.length > 0
        ? `${subsection.gridInfo.columns.length} columns`
        : 'unknown columns';
    return `GRID${refPart}: ${subsection.gridInfo.totalRows} rows, ${colInfo}`;
  }

  const allElements = collectAllElementsFlat(subsection);
  const elementNames = allElements.map((el) => el.label || el.name || el.role).filter(Boolean);

  const heading = subsection.heading || subsection.role;
  if (elementNames.length === 0) {
    return `${heading}${refPart}`;
  }

  const MAX_PREVIEW_NAMES = 3;
  const previewNames = elementNames.slice(0, MAX_PREVIEW_NAMES);
  const remainingCount = elementNames.length - previewNames.length;
  const namesList = previewNames.join(', ');
  const moreNote = remainingCount > 0 ? `... +${remainingCount} more` : '';

  return `${heading}${refPart}: ${namesList}${moreNote}`;
}

/** Char threshold above which a non-active grid is collapsed to a summary. ~1000 tokens. */
export const LARGE_GRID_CHAR_THRESHOLD = 4000;

function formatCollapsedGrid(section: Section, indent: number): string {
  const grid = section.gridInfo!;
  const indentStr = '  '.repeat(indent);
  const lines: string[] = [];

  // Section heading (same pattern as other section types)
  const headingPrefix = section.level > 0 ? '#'.repeat(Math.min(section.level + 2, 6)) + ' ' : '';
  const refPart = section.ref ? ` [${section.ref}]` : '';
  lines.push(`${indentStr}${headingPrefix}${section.heading}${refPart}`);

  const colInfo = grid.columns.length > 0 ? `${grid.columns.length} columns` : 'unknown columns';
  lines.push(`${indentStr}  GRID [${grid.ref}]: ${grid.totalRows} rows, ${colInfo}`);

  if (grid.columns.length > 0) {
    const MAX_DISPLAY = 10;
    const names = grid.columns
      .slice(0, MAX_DISPLAY)
      .map((c) => c.name)
      .join(', ');
    const more =
      grid.columns.length > MAX_DISPLAY ? ` (+${grid.columns.length - MAX_DISPLAY} more)` : '';
    lines.push(`${indentStr}  Columns: ${names}${more}`);
  }

  lines.push(`${indentStr}  (use expand="${grid.ref}" for interactive element refs)`);
  lines.push(`${indentStr}  (use browser_extract_table to get full table data as CSV)`);

  return lines.join('\n');
}

function formatSectionOutput(section: Section, indent: number, showCollapsed: boolean): string {
  if (section.gridInfo) {
    const rendered = formatGridOutput(section.gridInfo, indent);
    if (rendered.length > LARGE_GRID_CHAR_THRESHOLD && !section.gridInfo.containsActive) {
      return formatCollapsedGrid(section, indent);
    }
    return rendered;
  }

  const indentStr = '  '.repeat(indent);
  const lines: string[] = [];

  // Render datepicker sections compactly
  if (section.datepickerInfo) {
    const headingPrefix = section.level > 0 ? '#'.repeat(Math.min(section.level + 2, 6)) + ' ' : '';
    const refPart = section.ref ? ` [${section.ref}]` : '';
    lines.push(`${indentStr}${headingPrefix}${section.heading}${refPart}`);
    if (section.contextAnnotations) {
      lines.push(...formatContextAnnotations(section.contextAnnotations, indent));
    }
    lines.push(...formatDatepicker(section.datepickerInfo, indent));
    return lines.join('\n');
  }

  // Render content card sections
  if (section.contentCards && section.contentCards.length > 0) {
    const headingPrefix = section.level > 0 ? '#'.repeat(Math.min(section.level + 2, 6)) + ' ' : '';
    const refPart = section.ref ? ` [${section.ref}]` : '';
    lines.push(`${indentStr}${headingPrefix}${section.heading}${refPart}`);
    if (section.contextAnnotations) {
      lines.push(...formatContextAnnotations(section.contextAnnotations, indent));
    }
    lines.push(...formatContentCards(section.contentCards, indent));
    // Render any leftover non-card siblings (e.g., a trailing action button
    // after a card list). Pre-fix, these were silently dropped.
    for (const el of section.elements) {
      lines.push(formatElementLine(el, indent + 1));
    }
    for (const subsection of section.subsections) {
      const subsectionOutput = formatSectionOutput(subsection, indent + 1, showCollapsed);
      if (subsectionOutput) {
        lines.push('');
        lines.push(subsectionOutput);
      }
    }
    return lines.join('\n');
  }

  const headingPrefix = section.level > 0 ? '#'.repeat(Math.min(section.level + 2, 6)) + ' ' : '';
  const refPart = section.ref ? ` [${section.ref}]` : '';
  const dismissPart = section.dismissActionRef ? ` [dismiss=${section.dismissActionRef}]` : '';
  const focusedPart = section.isFocused ? ' [FOCUSED]' : '';
  const backgroundPart = section.isBackground ? ' [BACKGROUND]' : '';
  const activePart = section.containsActive && !section.isFocused ? ' ← ACTIVE' : '';
  lines.push(
    `${indentStr}${headingPrefix}${section.heading}${refPart}${dismissPart}${focusedPart}${backgroundPart}${activePart}`
  );

  // Render context annotations before elements
  if (section.contextAnnotations) {
    lines.push(...formatContextAnnotations(section.contextAnnotations, indent));
  }

  if (section.collapsed && !showCollapsed) {
    if (section.isBackground) {
      if (section.ref) {
        lines.push(
          `${indentStr}  (${section.elementCount} elements - use expand="${section.ref}" to see content)`
        );
      } else {
        lines.push(`${indentStr}  (${section.elementCount} elements)`);
      }
      return lines.join('\n');
    }

    const MAX_DIRECT_NONINTERACTIVE = 4;
    const previewElements: FormattedElement[] = [];
    let droppedNonInteractive = 0;
    let nonInteractiveShown = 0;
    for (const el of section.elements) {
      if (INTERACTIVE_ROLES.has(el.role)) {
        // Always preserve interactive affordances in collapsed sections — a
        // disabled toolbar button or a hidden action carries the highest signal
        // for an agent reasoning about what controls exist on the surface.
        previewElements.push(el);
        continue;
      }
      if (nonInteractiveShown < MAX_DIRECT_NONINTERACTIVE) {
        previewElements.push(el);
        nonInteractiveShown++;
      } else {
        droppedNonInteractive++;
      }
    }

    for (const el of previewElements) {
      lines.push(formatElementLine(el, indent + 1));
    }

    if (droppedNonInteractive > 0) {
      lines.push(`${indentStr}  ... +${droppedNonInteractive} more elements`);
    }

    for (const subsection of section.subsections) {
      if (subsection.gridInfo) {
        lines.push(...formatCollapsedGridPreview(subsection.gridInfo, indent + 1));
      } else {
        lines.push(`${indentStr}  ${formatSubsectionSummary(subsection)}`);
      }
    }

    if (section.ref && (droppedNonInteractive > 0 || section.subsections.length > 0)) {
      lines.push(`${indentStr}  (use expand="${section.ref}" to see all)`);
    }

    return lines.join('\n');
  }

  for (const el of section.elements) {
    lines.push(formatElementLine(el, indent + 1));
  }

  for (const subsection of section.subsections) {
    const subsectionOutput = formatSectionOutput(subsection, indent + 1, showCollapsed);
    if (subsectionOutput) {
      lines.push('');
      lines.push(subsectionOutput);
    }
  }

  return lines.join('\n');
}

function countRenderedElements(sections: Section[]): number {
  let count = 0;
  for (const section of sections) {
    count += section.elements.length;
    if (section.gridInfo) {
      count += countRenderedGridItems(section.gridInfo);
    }
    count += countRenderedElements(section.subsections);
  }
  return count;
}

function countTotalSections(sections: Section[]): number {
  let count = sections.length;
  for (const section of sections) {
    count += countTotalSections(section.subsections);
  }
  return count;
}

function generateFrontmatter(
  elementCount: number,
  sectionCount: number,
  activeElement: SnapshotElement | null,
  dialogStack: string[],
  searchMatches?: SearchMatch[],
  totalMatchCount?: number,
  snapshotFilePath?: string,
  probeResult?: SemanticSnapshotOptions['probeResult'],
  currentTime?: string,
  capturedToasts?: SemanticSnapshotOptions['capturedToasts'],
  consoleErrors?: SemanticSnapshotOptions['consoleErrors'],
  failedRequests?: SemanticSnapshotOptions['failedRequests'],
  hintCount?: number,
  unlabeledIconRefs?: string[]
): string {
  const activeLabel = activeElement
    ? (FORM_INPUT_ROLES.has(activeElement.role) ||
        activeElement.role === 'button' ||
        activeElement.role === 'link') &&
      (activeElement.attributes['expanded'] === 'true' ||
        activeElement.attributes['aria-expanded'] === 'true')
      ? getDisplayText(activeElement, { overrideLabel: composeTriggerLabel(activeElement) })
      : getDisplayText(activeElement)
    : '';
  const activeDesc = activeElement
    ? `${activeElement.ref} ${activeElement.role} "${activeLabel}"`
    : 'None';

  const dialogStackLine =
    dialogStack.length > 0 ? `\nDIALOG STACK: ${dialogStack.join(' → ')}` : '';

  let toastSection = '';
  if (capturedToasts && capturedToasts.length > 0) {
    const toastLines = capturedToasts.map((t) => {
      const ttl =
        t.snapshotsRemaining !== undefined ? ` (expires in ${t.snapshotsRemaining} snapshots)` : '';
      return `  - [${t.role}] "${t.text}"${ttl}`;
    });
    toastSection = `\nTOAST NOTIFICATIONS:\n${toastLines.join('\n')}`;
  }

  let consoleErrorSection = '';
  if (consoleErrors && consoleErrors.length > 0) {
    const shown = consoleErrors.slice(0, 5);
    const errorLines = shown.map((e) => `  - ${e.text}`);
    const moreNote = consoleErrors.length > 5 ? `\n  ... and ${consoleErrors.length - 5} more` : '';
    consoleErrorSection = `\nCONSOLE ERRORS (new since last snapshot):\n${errorLines.join('\n')}${moreNote}`;
  }

  let failedRequestSection = '';
  if (failedRequests && failedRequests.length > 0) {
    const shown = failedRequests.slice(0, 5);
    const reqLines = shown.map((r) => `  - [${r.status}] ${r.method} ${r.url}`);
    const moreNote =
      failedRequests.length > 5 ? `\n  ... and ${failedRequests.length - 5} more` : '';
    failedRequestSection = `\nFAILED REQUESTS (new since last snapshot):\n${reqLines.join('\n')}${moreNote}`;
  }

  let searchSection = '';
  if (searchMatches && searchMatches.length > 0) {
    const hasContextInfo = searchMatches.some((m) => m.context);
    const focusedCount = searchMatches.filter((m) => m.context === 'focused').length;

    const matchLines = searchMatches.map((m, i) => {
      const idx = i + 1;
      const color = COLOR_NAMES[i % COLOR_NAMES.length];
      const coords =
        m.centerX !== undefined && m.centerY !== undefined ? ` @ (${m.centerX}, ${m.centerY})` : '';
      const interactiveTag = m.isInteractive ? ' [interactive]' : '';
      const contextTag =
        m.context === 'focused' ? ' [FOCUSED]' : m.context === 'background' ? ' [BACKGROUND]' : '';

      let line = `  ${idx}. [${color}] [${m.ref}] ${m.role}${interactiveTag}${contextTag} "${m.label}"${coords}`;
      if (m.context === 'background' && m.sectionPath) {
        line += `\n      in: ${m.sectionPath}`;
      }
      return line;
    });

    const displayedCount = searchMatches.length;
    const total = totalMatchCount ?? displayedCount;
    const matchCountText =
      total > displayedCount
        ? `top ${displayedCount} of ${total} matches`
        : `${displayedCount} matches`;
    const sortNote =
      hasContextInfo && focusedCount > 0
        ? ' - focused results first'
        : ' - colors match screenshot';
    searchSection = `\nSEARCH RESULTS (${matchCountText}${sortNote}):\n${matchLines.join('\n')}`;
  }

  let typeActionHint = '';
  if (activeElement && FORM_INPUT_ROLES.has(activeElement.role)) {
    typeActionHint = `\n  - Type in active field: browser_type ref=${activeElement.ref} text="..."`;
  }

  let probeSection = '';
  if (probeResult) {
    const refInfo = probeResult.ref ? `[${probeResult.ref}]` : '[no ref]';
    const roleInfo = probeResult.elementInfo.role || probeResult.elementInfo.tagName;
    const labelInfo =
      probeResult.label || probeResult.elementInfo.ariaLabel || probeResult.elementInfo.id || '';
    const coordInfo = probeResult.boundingBox
      ? ` @ (${Math.round(probeResult.boundingBox.x + probeResult.boundingBox.width / 2)}, ${Math.round(probeResult.boundingBox.y + probeResult.boundingBox.height / 2)})`
      : '';
    const clickHint = probeResult.ref
      ? `\n  Click with: browser_click ref="${probeResult.ref}"`
      : '';

    probeSection = `\nPROBE RESULT:\n  [magenta] ${refInfo} ${roleInfo} "${labelInfo}"${coordInfo}${clickHint}`;
  }

  let fullTreeFileSection = '';
  if (snapshotFilePath) {
    fullTreeFileSection = `\nFULL TREE FILE: ${snapshotFilePath}\n  Use bash or python_execute to grep/parse this file for detailed element analysis.`;
  }

  const browserTime = currentTime ?? new Date().toISOString();
  const unlabeledIconHint =
    unlabeledIconRefs && unlabeledIconRefs.length > 0
      ? `\nHINTS:\n  - Unlabeled icon buttons: ${unlabeledIconRefs.length} found. To inspect one visually, call browser_screenshot ref="${unlabeledIconRefs[0]}" returnImage=true.`
      : '';

  return `---
SEMANTIC SNAPSHOT - ${elementCount} elements in ${sectionCount} sections
BROWSER TIME: ${browserTime} (UTC)
FORMAT: Sections grouped by headings. [ref] tags identify clickable elements.
ACTIVE ELEMENT: ${activeDesc}${dialogStackLine}${toastSection}${consoleErrorSection}${failedRequestSection}${searchSection}${probeSection}${fullTreeFileSection}${hintCount && hintCount > 0 ? `\nHINTS: ${hintCount} element hint${hintCount === 1 ? '' : 's'} active on this page. Use browser_snapshot hintDetail=ref for details.` : ''}${unlabeledIconHint}
ACTIONS:
  - Click/type using [ref] values (e.g., browser_click ref=e123)${typeActionHint}
  - Click using coordinates from search results (e.g., browser_click x=450 y=320)
  - expand(ref) - show full details for a collapsed section
  - Use bash or python_execute on the FULL TREE FILE for detailed element analysis
---`;
}

function collectUnlabeledIconRefs(sections: readonly Section[]): string[] {
  const refs: string[] = [];
  const walk = (section: Section): void => {
    if (section.gridInfo) {
      for (const row of section.gridInfo.rows) {
        for (const action of row.rowActions ?? []) {
          if (action.label === '(unlabeled icon; see HINTS)') refs.push(action.ref);
        }
      }
    }
    for (const subsection of section.subsections) walk(subsection);
  };
  for (const section of sections) walk(section);
  return refs;
}

/**
 * Scan all rendered elements across sections for duplicate labels.
 * When duplicates are found, append positional disambiguators like "(1st)", "(2nd)"
 * or section context to help the agent distinguish them.
 */
/**
 * Disambiguate elements that share a label (e.g. multiple "Save" buttons).
 *
 * Two emission modes:
 *   - **v1 (default)**: mutate `el.label` / `el.name` to append the
 *     disambiguator suffix (e.g. `"Save"` → `"Save (1st)"`). Render reads
 *     the label as-is.
 *   - **v2 (substrate-v2 inputs)**: write to `el.displayDecoration` instead,
 *     leaving `el.label` / `el.name` byte-identical to the substrate.
 *     `formatElementLine` concatenates the decoration when emitting.
 *
 * Same end render output either way; the v2 path just keeps the substrate
 * fields untouched so downstream consumers can compute against the canonical
 * name and the formatter owns the presentation-only suffix.
 */
function disambiguateDuplicateLabels(sections: Section[], useDisplayDecoration: boolean): void {
  // Collect all elements with their section context
  const elementEntries: Array<{ el: FormattedElement; sectionHeading: string }> = [];

  function collectElements(section: Section): void {
    for (const el of section.elements) {
      elementEntries.push({ el, sectionHeading: section.heading });
    }
    for (const sub of section.subsections) {
      collectElements(sub);
    }
  }
  for (const section of sections) {
    collectElements(section);
  }

  // Labels that are already generic/fallback and not worth disambiguating
  const SKIP_LABELS = new Set(['(icon)', 'button', 'link', 'generic']);

  // Group by label (only interactive elements with labels worth disambiguating)
  const byLabel = new Map<string, Array<{ el: FormattedElement; sectionHeading: string }>>();
  for (const entry of elementEntries) {
    const label = entry.el.label || entry.el.name;
    if (!label || label.length === 0) continue;
    if (SKIP_LABELS.has(label)) continue;
    // Only disambiguate interactive elements (buttons, links, inputs, etc.)
    if (!entry.el.ref) continue;
    const existing = byLabel.get(label);
    if (existing) {
      existing.push(entry);
    } else {
      byLabel.set(label, [entry]);
    }
  }

  // Disambiguate groups with duplicates
  for (const [, entries] of byLabel) {
    if (entries.length < 2) continue;

    // Try section-based disambiguation first
    const distinctSections = new Set(entries.map((e) => e.sectionHeading));
    if (distinctSections.size > 1) {
      // Elements are in different sections — append section context
      for (const entry of entries) {
        const suffix = ` (in ${entry.sectionHeading})`;
        applyDisambiguator(entry.el, suffix, useDisplayDecoration);
      }
    } else {
      // Same section — use positional disambiguators
      const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const ordinal = i < ordinals.length ? ordinals[i] : `${i + 1}th`;
        const suffix = ` (${ordinal})`;
        applyDisambiguator(entry.el, suffix, useDisplayDecoration);
      }
    }
  }
}

/**
 * Apply a disambiguator suffix to an element. Branches on substrate version:
 * v1 mutates the label/name in place, v2 writes to displayDecoration.
 */
function applyDisambiguator(
  el: FormattedElement,
  suffix: string,
  useDisplayDecoration: boolean
): void {
  if (useDisplayDecoration) {
    el.displayDecoration = (el.displayDecoration ?? '') + suffix;
    return;
  }
  if (el.label) {
    el.label = `${el.label}${suffix}`;
  } else if (el.name) {
    el.name = `${el.name}${suffix}`;
  }
}

/**
 * Match stored element hints to current snapshot elements by fingerprint hash,
 * then attach hint metadata to the corresponding FormattedElements.
 */
function attachElementHints(
  sections: Section[],
  parsedElements: SnapshotElement[],
  hints: Map<string, ElementHintAnnotation>
): { matchedCount: number; matchedHintIds: string[] } {
  const fpMap = fingerprintAllElements(parsedElements);

  const refToHint = new Map<string, ElementHintAnnotation>();
  const matchedHintIds: string[] = [];
  for (const [hash, hint] of hints) {
    const match = fpMap.get(hash);
    if (match) {
      refToHint.set(match.ref, hint);
      matchedHintIds.push(hint.id);
    }
  }

  if (refToHint.size === 0) {
    return { matchedCount: 0, matchedHintIds: [] };
  }

  function walkSections(secs: Section[]): void {
    for (const section of secs) {
      for (const el of section.elements) {
        const hint = refToHint.get(el.ref);
        if (hint) {
          el.hint = hint.frontmatter;
          el.hintCategory = hint.category;
          el.hintHasBody = hint.hasBody;
          el.hintElement = hint.element;
          el.hintAnchoredToParent = hint.element !== el.name && hint.element !== el.label;
        }
      }
      walkSections(section.subsections);
    }
  }

  walkSections(sections);
  return { matchedCount: refToHint.size, matchedHintIds };
}

export function formatSemanticSnapshot(yaml: string, options?: SemanticSnapshotOptions): string {
  if (!yaml || yaml.trim() === '') {
    return '---\nSEMANTIC SNAPSHOT - 0 elements\nPage appears empty or not loaded.\n---';
  }

  const elements = parseSnapshot(yaml);
  if (elements.length === 0) {
    return '---\nSEMANTIC SNAPSHOT - 0 elements\nFailed to parse snapshot.\n---';
  }

  const activeElement = findActiveElement(elements);
  const sections = buildSectionTree(elements);
  const { sorted: sortedSections, dialogStack } = sortSectionsByFocus(sections);
  const unlabeledIconRefs = collectUnlabeledIconRefs(sortedSections);

  let sortedMatches: SearchMatch[] | undefined;
  if (options?.searchMatches && options.searchMatches.length > 0) {
    const matchedRefs = new Set(options.searchMatches.map((m) => m.ref));
    markMatchingSections(sortedSections, matchedRefs);
    populateSearchContext(options.searchMatches, sortedSections);
    sortedMatches = sortMatchesByContext(options.searchMatches);
    sortedMatches.forEach((m, i) => {
      m.index = i + 1;
    });
  }

  const hasActiveElement = activeElement !== null;
  const hasSearchMatches = sortedMatches && sortedMatches.length > 0;
  if (!hasActiveElement && !hasSearchMatches) {
    // Keep grid sections governed by their own size threshold. Only reopen
    // top-level non-grid shells when nothing meaningful has focus; nested
    // collapsed sections still render compact subsection previews.
    for (const section of sortedSections) {
      if (section.collapsed && !section.gridInfo) {
        section.collapsed = false;
      }
    }
  }

  if (options?.probeResult?.ref) {
    markMatchingSections(sortedSections, new Set([options.probeResult.ref]));
  }

  // Post-process: disambiguate duplicate labels within the snapshot.
  // Substrate-v2 captures route the disambiguator suffix into a separate
  // `displayDecoration` field so substrate-derived `name`/`label` stay
  // canonical; v1 captures still mutate them in place to keep render
  // output byte-identical to existing snapshots.
  disambiguateDuplicateLabels(sortedSections, isSubstrateV2(elements));

  let matchedHintCount = 0;
  if (options?.elementHints && options.elementHints.size > 0) {
    const hintResult = attachElementHints(sortedSections, elements, options.elementHints);
    matchedHintCount = hintResult.matchedCount;
    if (options.matchedHintIds === undefined) {
      options.matchedHintIds = hintResult.matchedHintIds;
    }
  }

  const elementCount = countRenderedElements(sortedSections);
  const sectionCount = countTotalSections(sortedSections);

  const lines: string[] = [];
  lines.push(
    generateFrontmatter(
      elementCount,
      sectionCount,
      activeElement,
      dialogStack,
      sortedMatches,
      options?.totalMatchCount,
      options?.snapshotFilePath,
      options?.probeResult,
      options?.currentTime,
      options?.capturedToasts,
      options?.consoleErrors,
      options?.failedRequests,
      matchedHintCount,
      unlabeledIconRefs
    )
  );
  lines.push('');

  const editingContextBlock = formatEditingContexts(extractEditingContextsFromElements(elements));
  if (editingContextBlock) {
    lines.push(editingContextBlock);
    lines.push('');
  }

  for (const section of sortedSections) {
    const sectionOutput = formatSectionOutput(section, 0, false);
    if (sectionOutput) {
      lines.push(sectionOutput);
      lines.push('');
    }
  }

  const result = lines.join('\n').trim();

  return result;
}

export function expandSection(yaml: string, sectionRef: string): string {
  if (!yaml || yaml.trim() === '') {
    return '---\nSEMANTIC SNAPSHOT - 0 elements\nPage appears empty or not loaded.\n---';
  }

  const elements = parseSnapshot(yaml);
  if (elements.length === 0) {
    return '---\nSEMANTIC SNAPSHOT - 0 elements\nFailed to parse snapshot.\n---';
  }

  const activeElement = findActiveElement(elements);
  const sections = buildSectionTree(elements);
  const { sorted: sortedSections, dialogStack } = sortSectionsByFocus(sections);
  expandSectionByRef(sortedSections, sectionRef);

  const elementCount = countRenderedElements(sortedSections);
  const sectionCount = countTotalSections(sortedSections);

  const lines: string[] = [];
  lines.push(generateFrontmatter(elementCount, sectionCount, activeElement, dialogStack));
  lines.push('');

  for (const section of sortedSections) {
    const sectionOutput = formatSectionOutput(section, 0, false);
    if (sectionOutput) {
      lines.push(sectionOutput);
      lines.push('');
    }
  }

  return lines.join('\n').trim();
}

function formatExpandedSectionOutput(section: Section): string {
  const lines: string[] = [];
  const subsectionInfo =
    section.subsections.length > 0 ? `, ${section.subsections.length} subsections` : '';
  lines.push(`---`);
  lines.push(`EXPANDED SECTION: ${section.heading} [${section.ref}]`);
  lines.push(`Contains ${section.elementCount} elements${subsectionInfo}`);
  lines.push(`---`);
  lines.push('');

  // Render grid content when expanding a grid section directly (show all rows)
  if (section.gridInfo) {
    lines.push(formatGridOutput(section.gridInfo, 0, true));
    return lines.join('\n').trim();
  }

  for (const el of section.elements) {
    lines.push(formatElementLine(el, 0));
  }

  for (const subsection of section.subsections) {
    lines.push('');
    lines.push(formatSectionOutput(subsection, 0, false));
  }

  return lines.join('\n').trim();
}

export function expandSectionOnly(yaml: string, sectionRef: string): string {
  if (!yaml || yaml.trim() === '') {
    return `---\nEXPANDED SECTION: Not found\nPage appears empty or not loaded.\n---`;
  }

  const elements = parseSnapshot(yaml);
  if (elements.length === 0) {
    return `---\nEXPANDED SECTION: Not found\nFailed to parse snapshot.\n---`;
  }

  const sections = buildSectionTree(elements);
  const targetSection = findSectionByRef(sections, sectionRef);
  if (!targetSection) {
    return `---\nEXPANDED SECTION: Not found\nSection with ref="${sectionRef}" not found in snapshot.\n---`;
  }

  targetSection.collapsed = false;
  return formatExpandedSectionOutput(targetSection);
}

/**
 * Identify grid refs that will be collapsed due to rendered size exceeding the threshold.
 * Accepts optional pre-parsed elements to avoid redundant YAML parsing.
 */
export function identifyLargeGridRefs(
  yaml: string,
  preParsedElements?: SnapshotElement[]
): string[] {
  if (!yaml || yaml.trim() === '') return [];

  const elements = preParsedElements ?? parseSnapshot(yaml);
  if (elements.length === 0) return [];

  const sections = buildSectionTree(elements);
  const refs: string[] = [];

  const walk = (secs: Section[]) => {
    for (const s of secs) {
      if (s.gridInfo?.ref && !s.gridInfo.containsActive) {
        const rendered = formatGridOutput(s.gridInfo, 0);
        if (rendered.length > LARGE_GRID_CHAR_THRESHOLD) {
          refs.push(s.gridInfo.ref);
        }
      }
      walk(s.subsections);
    }
  };
  walk(sections);
  return refs;
}
